import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import IconRefresh from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Paper
} from '@mui/material';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import NavBarAdm from './navbarAdm';

// Define lines array (updated list)
const LINES = [
  "50", "53", "X53", "54", "55", "58", "X58", "59", "89", "N11"
];

export const UmleitungenDelete = () => {
  const navigate = useNavigate();
  const [umleitungen, setUmleitungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    item: null
  });
  
  // Filter state
  const [selectedLine, setSelectedLine] = useState('');

  // Filter function
  const filteredUmleitungen = umleitungen.filter(umleitung => {
    if (!selectedLine) return true;
    return umleitung.line && umleitung.line.includes(selectedLine);
  });

  // Handle line filter click
  const handleLineClick = (line) => {
    setSelectedLine(selectedLine === line ? '' : line);
  };

  // Fetch all Umleitungen from Firebase
  const fetchUmleitungen = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'Umleitungen'));
      const umleitungenList = [];
      
      querySnapshot.forEach((doc) => {
        umleitungenList.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      // Sort by creation date (newest first)
      umleitungenList.sort((a, b) => {
        const dateA = a.createdAt?.toDate() || new Date(0);
        const dateB = b.createdAt?.toDate() || new Date(0);
        return dateB - dateA;
      });
      
      setUmleitungen(umleitungenList);
      setMessage(`${umleitungenList.length} Umleitungen gefunden`);
      setMessageType('info');
    } catch (error) {
      console.error('Error fetching Umleitungen:', error);
      setMessage(`Fehler beim Laden: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  // Navigate to edit page
  const handleEdit = (id) => {
    navigate(`/adm/umleitung/edit/${id}`);
  };

  // Delete Umleitung from Firebase
  const handleDelete = async (id) => {
    try {
      setDeleting(id);
      await deleteDoc(doc(db, 'Umleitungen', id));
      
      // Remove from local state
      setUmleitungen(prev => prev.filter(item => item.id !== id));
      
      setMessage('Umleitung erfolgreich gelöscht!');
      setMessageType('success');
    } catch (error) {
      console.error('Error deleting Umleitung:', error);
      setMessage(`Fehler beim Löschen: ${error.message}`);
      setMessageType('error');
    } finally {
      setDeleting(null);
      setDeleteDialog({ open: false, item: null });
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (item) => {
    setDeleteDialog({
      open: true,
      item: item
    });
  };

  // Close delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      item: null
    });
  };

  // Load data on component mount
  useEffect(() => {
    fetchUmleitungen();
  }, []);

  // Refresh data
  const handleRefresh = () => {
    fetchUmleitungen();
  };

  return (
    <>
    <NavBarAdm />
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Umleitungen verwalten ({filteredUmleitungen.length})
          </Typography>
          <Button 
            variant="outlined" 
            onClick={handleRefresh}
            disabled={loading}
          >
            <IconRefresh />
          </Button>
        </Box>
        
        {/* Line Filter */}
        <Box sx={{ mb: 3, mx: 1 }}>
          <Typography variant="h6" gutterBottom>
            Filter nach Linie:
          </Typography>
          <Box sx={{ 
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: 'center',
            maxWidth: '800px',
            mx: 'auto'
          }}>
            {/* ALLE chip */}
            <Chip
              key="alle"
              label="ALLE"
              onClick={() => setSelectedLine('')}
              variant={!selectedLine ? "filled" : "outlined"}
              color={!selectedLine ? "primary" : "default"}
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
                height: "36px",
                minWidth: "fit-content",
                px: 1,
                cursor: "pointer",
                border: "2px solid",
                borderColor: !selectedLine ? "primary.main" : "#ccc",
                '&:hover': {
                  backgroundColor: !selectedLine ? "primary.dark" : "action.hover",
                  borderColor: "primary.main"
                },
                '& .MuiChip-label': {
                  px: 1,
                  py: 0.5
                }
              }}
            />
            
            {LINES.map(line => (
              <Chip
                key={line}
                label={line}
                onClick={() => handleLineClick(line)}
                variant={selectedLine === line ? "filled" : "outlined"}
                color={selectedLine === line ? "primary" : "default"}
                sx={{
                  fontSize: "1rem",
                  fontWeight: "bold",
                  height: "36px",
                  minWidth: "fit-content",
                  px: 1,
                  cursor: "pointer",
                  border: "2px solid",
                  borderColor: selectedLine === line ? "primary.main" : "#ccc",
                  '&:hover': {
                    backgroundColor: selectedLine === line ? "primary.dark" : "action.hover",
                    borderColor: "primary.main"
                  },
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'visible'
                  }
                }}
              />
            ))}
          </Box>
        </Box>
        
        <Divider sx={{ mb: 3 }} />

        {message && (
          <Alert severity={messageType} sx={{ mb: 3 }}>
            {message}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredUmleitungen.length === 0 ? (
          <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
            {selectedLine 
              ? `Keine Umleitungen für Linie ${selectedLine} gefunden.`
              : 'Keine Umleitungen gefunden.'
            }
          </Typography>
        ) : (
          <Grid container spacing={3}>
            {filteredUmleitungen.map((umleitung) => (
              <Grid item xs={12} md={6} lg={4} key={umleitung.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        {umleitung.title || 'Kein Titel'}
                      </Typography>
                      {umleitung.show ? (
                        <VisibilityIcon color="success" />
                      ) : (
                        <VisibilityOffIcon color="disabled" />
                      )}
                    </Box>

                    {umleitung.number && (
                      <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
                        Nummer: {umleitung.number}
                      </Typography>
                    )}

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {umleitung.dateStart} - {umleitung.dateEnd}
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {umleitung.description && umleitung.description.length > 100 
                        ? `${umleitung.description.substring(0, 100)}...`
                        : umleitung.description || 'Keine Beschreibung'
                      }
                    </Typography>

                    {/* Lines */}
                    {umleitung.line && umleitung.line.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Linien:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {umleitung.line.slice(0, 3).map((line, index) => (
                            <Chip
                              key={index}
                              label={line}
                              size="small"
                              variant="outlined"
                              color={selectedLine === line ? "primary" : "default"}
                              sx={{ 
                                fontWeight: selectedLine === line ? "bold" : "normal"
                              }}
                            />
                          ))}
                          {umleitung.line.length > 3 && (
                            <Chip
                              label={`+${umleitung.line.length - 3} mehr`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    {/* Images */}
                    {umleitung.image && umleitung.image.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                          Bilder: {umleitung.image.length}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {umleitung.image.slice(0, 2).map((img, index) => (
                            <img
                              key={index}
                              src={img}
                              alt={`Bild ${index + 1}`}
                              style={{
                                width: '60px',
                                height: '60px',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                              }}
                            />
                          ))}
                          {umleitung.image.length > 2 && (
                            <Box
                              sx={{
                                width: '60px',
                                height: '60px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5',
                                borderRadius: '4px',
                                border: '1px solid #ddd'
                              }}
                            >
                              <Typography variant="caption">
                                +{umleitung.image.length - 2}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    )}

                    {/* Metadata */}
                    <Typography variant="caption" color="text.secondary">
                      ID: {umleitung.id}
                    </Typography>
                    {umleitung.createdAt && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        Erstellt: {umleitung.createdAt.toDate().toLocaleDateString('de-DE')}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(umleitung.id)}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => openDeleteDialog(umleitung)}
                      disabled={deleting === umleitung.id}
                      size="small"
                    >
                      {deleting === umleitung.id ? 'Löschen...' : 'Löschen'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={closeDeleteDialog}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Umleitung löschen
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Sind Sie sicher, dass Sie diese Umleitung löschen möchten?
              <br />
              <br />
              <strong>Titel:</strong> {deleteDialog.item?.title || 'Kein Titel'}
              <br />
              <strong>Nummer:</strong> {deleteDialog.item?.number || 'Keine Nummer'}
              <br />
              <strong>Zeitraum:</strong> {deleteDialog.item?.dateStart} - {deleteDialog.item?.dateEnd}
              <br />
              <br />
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteDialog} disabled={deleting}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => handleDelete(deleteDialog.item?.id)} 
              color="error" 
              variant="contained"
              disabled={deleting}
              autoFocus
            >
              {deleting ? 'Löschen...' : 'Löschen'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
    </>
  );
};